import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown } from 'lucide-react';
import './BeamPropertiesPro.css';
import { LiaLessThanSolid } from "react-icons/lia";

const Dim = ({ x1, y1, x2, y2, label, offset = 12, side = 'top' }) => {
    let lx1 = x1, ly1 = y1, lx2 = x2, ly2 = y2;
    let tx = (x1 + x2) / 2, ty = (y1 + y2) / 2;
    if (side === 'top') { ly1 -= offset; ly2 -= offset; ty -= 4; }
    else if (side === 'bottom') { ly1 += offset; ly2 += offset; ty += 12; }
    else if (side === 'left') { lx1 -= offset; lx2 -= offset; tx -= 4; }
    else if (side === 'right') { lx1 += offset; lx2 += offset; tx += 12; }
    return (
        <g>
            <line x1={lx1} y1={ly1} x2={lx2} y2={ly2} className="dim-line" />
            <text x={tx} y={ty} className="dim-text" textAnchor="middle" dominantBaseline="middle" transform={(side === 'left' || side === 'right') ? `rotate(-90, ${tx}, ${ty})` : ''}>{label}</text>
        </g>
    );
};

const BeamPropertiesPro = () => {

    // ─── CONFIG & CONVERSIONS ───
    const FACTORS = {
        length: { mm: 1, cm: 10, m: 1000, in: 1 },
        area: { 'mm²': 1, 'cm²': 100, 'm²': 1000000, 'in²': 1 },
        inertia: { 'mm⁴': 1, 'cm⁴': 10000, 'm⁴': 1000000000000, 'in⁴': 1 },
        sectionModulus: { 'mm³': 1, 'cm³': 1000, 'm³': 1000000000, 'in³': 1 }
    };

    const UNIT_OPTIONS = {
        metric: {
            length: ['mm', 'cm', 'm'],
            area: ['mm²', 'cm²', 'm²'],
            inertia: ['mm⁴', 'cm⁴', 'm⁴'],
            sectionModulus: ['mm³', 'cm³', 'm³']
        },
        imperial: {
            length: ['in'],
            area: ['in²'],
            inertia: ['in⁴'],
            sectionModulus: ['in³']
        }
    };

    // ─── STATE ───
    const [section, setSection] = useState('square');
    const [units, setUnits] = useState('mm'); // Global system toggle: 'mm' (Metric) or 'in' (Imperial)
    const [values, setValues] = useState({
        b: 0, h: 0, t: 0, bi: 0, hi: 0,
        w: 0, tf: 0, tw: 0, radius: 0
    });

    const [valueUnits, setValueUnits] = useState({
        b: 'mm', h: 'mm', t: 'mm', bi: 'mm', hi: 'mm', r: 'mm', ri: 'mm', w: 'mm', tf: 'mm', tw: 'mm', radius: 'mm'
    });

    const [resultUnits, setResultUnits] = useState({
        A: 'mm²', ybar: 'mm', xbar: 'mm', Ixx: 'mm⁴', Iyy: 'mm⁴', Sx: 'mm³', Sy: 'mm³', J: 'mm⁴'
    });

    const [results, setResults] = useState(null);
    const [activeFaq, setActiveFaq] = useState(null);

    // Toggle global system and reset local units
    const toggleSystem = (sys) => {
        setUnits(sys);
        const isMetric = sys === 'mm';
        const lengthDefault = isMetric ? 'mm' : 'in';
        const areaDefault = isMetric ? 'mm²' : 'in²';
        const inertiaDefault = isMetric ? 'mm⁴' : 'in⁴';
        const smDefault = isMetric ? 'mm³' : 'in³';

        setValueUnits({
            b: lengthDefault, h: lengthDefault, t: lengthDefault, bi: lengthDefault, hi: lengthDefault,
            w: lengthDefault, r: lengthDefault, ri: lengthDefault,
            tf: lengthDefault, tw: lengthDefault, radius: lengthDefault
        });

        setResultUnits({
            A: areaDefault, ybar: lengthDefault, xbar: lengthDefault,
            Ixx: inertiaDefault, Iyy: inertiaDefault,
            Sx: smDefault, Sy: smDefault, J: inertiaDefault
        });
    };

    // ─── CONFIG ───
    const sectionTypes = [
        { id: 'square', label: 'Square', icon: 'M5 5h14v14H5z' },
        { id: 'rectangle', label: 'Solid Rect', icon: 'M4 8h16v8H4z' },
        { id: 'rhs', label: 'Hollow Rect', icon: 'M4 6h16v12H4z M7 9h10v6H7z' },
        { id: 'ibeam', label: 'I-Beam', icon: 'M6 4h12v3H13v10h5v3H6v-3h5V7H6z' },
        { id: 'tsection', label: 'T-Section', icon: 'M6 4h12v3h-4.5v13h-3V7H6z' },
        { id: 'cchannel', label: 'C-Channel', icon: 'M18 4v3H9v10h9v3H6V4z' },
        { id: 'lsection', label: 'L-Section', icon: 'M6 4h4v12h10v4H6z' },
        { id: 'circle', label: 'Solid Circle', icon: 'M12 4a8 8 0 100 16 8 8 0 000-16z' },
        { id: 'pipe', label: 'Hollow Pipe', icon: 'M12 4a8 8 0 100 16 8 8 0 000-16z M12 8a4 4 0 100 8 4 4 0 000-8z' }
    ];

    // ─── CALCULATION ENGINE ───
    const calculate = () => {
        let A = 0, Ixx = 0, Iyy = 0, ybar = 0, xbar = 0, Sx = 0, Sy = 0, J = 0;

        // 1. Convert all values to base (mm or in)
        const v = {};
        Object.keys(values).forEach(key => {
            v[key] = values[key] * FACTORS.length[valueUnits[key]];
        });

        const { b, h, t, bi, hi, r: d_outer, ri: d_inner, w, tf, tw, radius } = v;

        switch (section) {
            case 'rectangle':
                A = b * h;
                ybar = h / 2;
                xbar = b / 2;
                Ixx = (b * Math.pow(h, 3)) / 12;
                Iyy = (h * Math.pow(b, 3)) / 12;
                Sx = Ixx / ybar;
                Sy = Iyy / xbar;
                J = ((b * Math.pow(h, 3)) / 3 - 0.21 * Math.pow(h, 4) + 0.0175 * Math.pow(h, 8) / Math.pow(b, 4));
                break;

            case 'square':
                A = b * b;
                ybar = b / 2;
                xbar = b / 2;
                Ixx = Math.pow(b, 4) / 12;
                Iyy = Ixx;
                Sx = Ixx / ybar;
                Sy = Sx;
                J = (9 * Math.pow(b, 4)) / 64;
                break;

            case 'rhs':
                A = (b * h) - (bi * hi);
                ybar = h / 2;
                xbar = b / 2;
                Ixx = (b * Math.pow(h, 3) - bi * Math.pow(hi, 3)) / 12;
                Iyy = (h * Math.pow(b, 3) - hi * Math.pow(bi, 3)) / 12;
                Sx = Ixx / ybar;
                Sy = Iyy / xbar;
                break;

            case 'circle':
                const r = d_outer;
                ybar = r;
                xbar = r;
                const momentOfInertiaValue = ((Math.PI * Math.pow(r, 4)) / 4);
                const SolidCirclesectionModulesValue = (momentOfInertiaValue / r);
                Ixx = momentOfInertiaValue
                Iyy = Ixx;
                Sx = SolidCirclesectionModulesValue;
                Sy = SolidCirclesectionModulesValue;
                J = ((Math.PI * Math.pow(r, 4)) / 2);
                break;

            case 'pipe':
                ybar = d_outer;
                xbar = d_outer;
                const pi = Math.PI;
                const parsedB = d_outer;
                const parsedD = d_inner;
                const HollowCirclemomentOfInertiamultiplyValue = ((Math.pow(parsedB, 4) - Math.pow(parsedD, 4)));
                const divdeinertiaValue = 4;
                const inertia = ((pi * HollowCirclemomentOfInertiamultiplyValue) / divdeinertiaValue).toFixed(4);

                Ixx = inertia;
                Iyy = Ixx;
                Sx = Ixx / ybar;
                Sy = Sx;
                break;

            case 'ibeam':
                // tw=tw
                // d = h
                // b = w
                // t = tf
                A = (tw * h) + 2 * (tf * w);
                ybar = (h / 2) + (tf);
                xbar = w / 2;

                const firstValueIx = (w * Math.pow((h - (-2 * tf)), 3));
                const subtractedValueIx = (w - tw) * Math.pow(h, 3);
                const IsectionmomentOfInertiaIx = ((firstValueIx - subtractedValueIx) / 12).toFixed(4);
                Ixx = IsectionmomentOfInertiaIx;

                const IsectionnumeratorIy = (Math.pow(w, 3) * tf) / 6;
                const IsectiondenominatorIy = (Math.pow(tw, 3) * h) / 12;
                const IsectionmomentOfInertiaIy = (IsectionnumeratorIy + IsectiondenominatorIy).toFixed(4);
                Iyy = IsectionmomentOfInertiaIy;
                Sx = Ixx / ybar;
                Sy = Iyy / xbar;

                const k1 = ((w * Math.pow(tf, 3)) / 3) - (0.21 * Math.pow(tf, 4)) - (0.0175 * (Math.pow(tf, 8) / Math.pow(w, 4)))
                const k2 = (h * Math.pow(tw, 3)) / 3
                const Dnumenator = (Math.pow((tf - (-radius)), 2) + radius * tw + Math.pow(tw, 2) / 4)
                const Ddenomenator = 2 * radius + tf
                const D = Dnumenator / Ddenomenator
                if (tf < tw) {
                    var t1 = tf
                    var t2 = tw
                } else {
                    var t1 = tw
                    var t2 = tf
                }
                const alpha = (t1 / t2) * (0.15 + (0.1 * (radius / tf)))
                const K = 2 * k1 + k2 + (2 * alpha * Math.pow(D, 4))

                J = K
                break;

            case 'tsection':
                A = (w * tf) + (h * tw);

                const tsection_numerator_yc = (w * Math.pow(tf, 2)) + ((tw * h) * (2 * tf + h));
                const tsection_denominator_yc = 2 * (tf * w + tw * h);
                const tsection_calculated_centroid_yc = (tsection_numerator_yc / tsection_denominator_yc);
                ybar = tsection_calculated_centroid_yc;
                xbar = w / 2;

                const term1 = w * Math.pow((h + tf), 3) - Math.pow(h, 3) * (w - tw);
                const term2 = 3;
                const term3 = A * Math.pow((h + tf - ybar), 2);
                const Ix = (term1 / term2) - term3;
                const Sxdenomenator = (h + tf) - ybar;
                const sectionModulesValue = (Ix / Sxdenomenator).toFixed(4);
                Ixx = Ix
                Iyy = (tf * Math.pow(w, 3) + (h - tf) * Math.pow(tw, 3)) / 12;
                Sx = sectionModulesValue
                Sy = Iyy / xbar;
                J = (1 / 3) * (w * Math.pow(tf, 3) + (h - tf) * Math.pow(tw, 3));
                break;

            case 'cchannel': {
                // w = Flange Width (b)
                // h = web height (d)
                // tf = Web thickness (t)
                // tw = Flange thickness (tw)
                A = w * tf + 2 * tw * h;
                const numeratorCentroidYc = w * tf * (tf / 2) + 2 * tw * h * (tf + h / 2);
                ybar = A > 0 ? numeratorCentroidYc / A : 0;
                xbar = w / 2;

                const Ix_top = (w * Math.pow(tf, 3)) / 3 + 2 * tw * h * (Math.pow(h, 2) / 12 + Math.pow(tf + h / 2, 2));
                Ixx = Ix_top - A * Math.pow(ybar, 2);
                Iyy = ((tf + h) * Math.pow(w, 3) - h * Math.pow(w - 2 * tw, 3)) / 12;

                const c_max = Math.max(ybar, h + tf - ybar);
                Sx = c_max > 0 ? Ixx / c_max : 0;
                Sy = xbar > 0 ? Iyy / xbar : 0;
                J = (1 / 3) * (w * Math.pow(tf, 3) + 2 * h * Math.pow(tw, 3));
                break;
            }

            case 'lsection': {
                // L-Section: b = flange width, h = total height (d), t = thickness
                const lb = b;
                const ld = h;
                const lt = t;
                A = lt * (lb + ld - lt);
                const xc_num = (lb * lb) + (ld * lt) - (lt * lt);
                const xc_den = 2 * (lb + ld - lt);
                xbar = xc_num / xc_den;
                const yc_num = (ld * ld) + (lb * lt) - (lt * lt);
                const yc_den = 2 * (lb + ld - lt);
                ybar = yc_num / yc_den;
                const ix_num = (lb * Math.pow(ld, 3)) - ((lb - lt) * Math.pow(ld - lt, 3));
                Ixx = (ix_num / 3) - (A * Math.pow(ld - ybar, 2));
                const iy_num = (ld * Math.pow(lb, 3)) - ((ld - lt) * Math.pow(lb - lt, 3));
                Iyy = (iy_num / 3) - (A * Math.pow(lb - xbar, 2));
                Sx = Ixx / (ld - ybar);
                Sy = Iyy / (lb - xbar);
                break;
            }
            default: break;
        }

        setResults({ A, Ixx, Iyy, ybar, xbar, Sx, Sy, J });
    };

    useEffect(() => {
        calculate();
    }, [section, values, valueUnits]);

    const handleInput = (key, val) => {
        setValues(prev => ({
            ...prev,
            [key]: parseFloat(val) || 0
        }));
    };

    const fmt = (val, type = 'length', unitKey = null) => {
        if (val === undefined || val === null || isNaN(val)) {
            return '0.00';
        }

        let converted = val;
        const targetUnit = unitKey || (type === 'length' ? units : '');

        if (FACTORS[type] && FACTORS[type][targetUnit]) {
            converted = val / FACTORS[type][targetUnit];
        }

        // Always use scientific notation for meter-based units
        const meterUnits = ['m', 'm²', 'm³', 'm⁴'];

        if (meterUnits.includes(targetUnit)) {
            return converted.toExponential(2);
        }

        return converted.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const handleUnitChange = (key, unit, isResult = false) => {
        if (isResult) {
            setResultUnits(prev => ({
                ...prev,
                [key]: unit
            }));
        } else {
            setValues(prev => {
                const currentValInBase =
                    prev[key] * FACTORS.length[valueUnits[key]];

                const newVal =
                    currentValInBase / FACTORS.length[unit];

                return {
                    ...prev,
                    [key]: newVal
                };
            });

            setValueUnits(prev => ({
                ...prev,
                [key]: unit
            }));
        }
    };
    const renderDiagram = () => {
        const S = 160;
        const { b, h, t, bi, hi, r: d_outer, di: d_inner, w, tf, tw, radius } = values;

        let path = '';
        let labels = [];

        if (section === 'rectangle') {
            const isLive = b > 0 && h > 0;
            const draw_b = isLive ? b : 1;
            const draw_h = isLive ? h : 1.5;
            const ratio = draw_h / draw_b;
            const dw = ratio > 1 ? S / ratio : S;
            const dh = ratio > 1 ? S : S * ratio;
            const x0 = 100 - dw / 2, y0 = 100 - dh / 2;
            path = `M${x0} ${y0} h${dw} v${dh} h-${dw} z`;
            labels.push(<Dim key="b" x1={x0} y1={y0 + 190} x2={x0 + dw} y2={y0 + 190} label={`b=${b}`} side="top" />);
            labels.push(<Dim key="d" x1={x0 - 12} y1={y0} x2={x0 - 12} y2={y0 + dh} label={`d=${h}`} side="left" />);
        } else if (section === 'square') {
            const isLive = b > 0;
            const dw = S;
            const dh = S;
            const x0 = 100 - dw / 2, y0 = 100 - dh / 2;
            path = `M${x0} ${y0} h${dw} v${dh} h-${dw} z`;
            labels.push(<Dim key="a2" x1={x0 - 12} y1={y0} x2={x0 - 12} y2={y0 + dh} label={`a=${b}`} side="left" />);
        } else if (section === 'rhs') {
            const isLive = b > 0 && h > 0 && bi > 0 && hi > 0;
            const draw_b = isLive ? b : 1;
            const draw_h = isLive ? h : 1.5;
            const draw_bi = isLive ? bi : 0.8;
            const draw_hi = isLive ? hi : 1.3;

            const ratio = draw_h / draw_b;
            const dw = ratio > 1 ? S / ratio : S;
            const dh = ratio > 1 ? S : S * ratio;
            const x0 = 100 - dw / 2, y0 = 100 - dh / 2;
            const innerW = Math.min(Math.max(draw_bi, 0), draw_b) / draw_b * dw;
            const innerH = Math.min(Math.max(draw_hi, 0), draw_h) / draw_h * dh;
            const innerX = x0 + (dw - innerW) / 2;
            const innerY = y0 + (dh - innerH) / 2;
            path = `M${x0} ${y0} h${dw} v${dh} h-${dw} z M${innerX} ${innerY} h${innerW} v${innerH} h-${innerW} z`;
            labels.push(<Dim key="b" x1={x0} y1={y0 - 2} x2={x0 + dw} y2={y0 - 2} label={`b=${b}`} side="top" />);
            labels.push(<Dim key="d" x1={x0 - 10} y1={y0} x2={x0 - 10} y2={y0 + dh} label={`d=${h}`} side="left" />);
            labels.push(<Dim key="bi" x1={innerX} y1={innerY + 10 + innerH} x2={innerX + innerW} y2={innerY + 10 + innerH} label={`bi=${bi}`} side="bottom" offset={24} />);
            labels.push(<Dim key="hi" x1={innerX + 10 + innerW} y1={innerY} x2={innerX + 10 + innerW} y2={innerY + innerH} label={`di=${hi}`} side="right" offset={20} />);
        } else if (section === 'circle') {
            const isLive = values.r > 0;
            const ro = S / 2;

            return (
                <g>
                    {/* Arrow Marker */}
                    <defs>
                        <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,6 L6,3 z" fill="#444" />
                        </marker>
                    </defs>
                    {/* Circle */}
                    <circle cx="100" cy="100" r={ro} fill="rgba(29,78,216,.10)" stroke="var(--accent)" strokeWidth="2" />
                    {/* Radius with Arrow */}
                    <line x1="100" y1="100" x2={100 - ro * 0.707} y2={100 - ro * 0.707} stroke="#444" strokeWidth="1.5" markerEnd="url(#arrow)" />
                    <text x={105 - ro * 0.42} y={100 - ro * 0.42} className="dim-text">r={values.r}</text>
                </g>
            );
        } else if (section === 'pipe') {
            const isLive = values.r > 0 && values.ri > 0;
            const draw_r = isLive ? values.r : 1;
            const draw_ri = isLive ? values.ri : 0.8;

            const ro = S / 2;
            const ri = (draw_ri / draw_r) * ro;

            return (
                <g>
                    {/* Arrow Marker */}
                    <defs>
                        <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,6 L6,3 z" fill="#444" />
                        </marker>
                    </defs>
                    {/* Outer */}
                    <circle cx="100" cy="100" r={ro} fill="rgba(29,78,216,.10)" stroke="var(--accent)" strokeWidth="2" />
                    {/* Inner */}
                    <circle cx="100" cy="100" r={ri} fill="#1d4fd819" stroke="var(--accent)" strokeWidth="2" />
                    {/* Outer Radius Arrow */}
                    <line x1="100" y1="100" x2={100 - ro * 0.707} y2={100 - ro * 0.707} stroke="#444" strokeWidth="1.5" markerEnd="url(#arrow)" />
                    <text x={95 - ro * 0.42} y={125 - ro * 0.42} className="dim-text">R={values.r}</text>
                    {/* Inner Radius Arrow */}
                    <line x1="100" y1="100" x2={100 + ri * 0.707} y2={100 - ri * 0.707} stroke="#444" strokeWidth="1.5" markerEnd="url(#arrow)" />
                    <text x={100 + ri * 0.45} y={115 - ri * 0.45} className="dim-text">Ri={values.ri}</text>
                </g>
            );
        } else if (section === 'ibeam') {
            const isLive = h > 0 && w > 0 && tf > 0 && tw > 0;
            const draw_h = isLive ? h : 2;
            const draw_w = isLive ? w : 1.5;
            const draw_tf = isLive ? tf : 0.2;
            const draw_tw = isLive ? tw : 0.2;
            const draw_r = isLive ? radius : 0; // radius is optional

            const ratio = draw_h / draw_w;
            const dw = ratio > 1 ? S / ratio : S;
            const dh = ratio > 1 ? S : S * ratio;

            const tfs = (draw_tf / draw_h) * dh;
            const tws = (draw_tw / draw_w) * dw;

            const rs =
                draw_r > 0
                    ? Math.min(
                        (draw_r / Math.min(draw_w, draw_h)) * Math.min(dw, dh),
                        10
                    )
                    : 0;

            const x0 = 100 - dw / 2;
            const y0 = 100 - dh / 2;

            const webLeft = 100 - tws / 2;
            const webRight = 100 + tws / 2;

            let path = '';

            if (draw_r > 0) {
                path = `
            M ${x0} ${y0}
            H ${x0 + dw}
            V ${y0 + tfs}
            H ${webRight + rs}
            A ${rs} ${rs} 0 0 0 ${webRight} ${y0 + tfs + rs}
            V ${y0 + dh - tfs - rs}
            A ${rs} ${rs} 0 0 0 ${webRight + rs} ${y0 + dh - tfs}
            H ${x0 + dw}
            V ${y0 + dh}
            H ${x0}
            V ${y0 + dh - tfs}
            H ${webLeft - rs}
            A ${rs} ${rs} 0 0 0 ${webLeft} ${y0 + dh - tfs - rs}
            V ${y0 + tfs + rs}
            A ${rs} ${rs} 0 0 0 ${webLeft - rs} ${y0 + tfs}
            H ${x0}
            Z
        `;
            } else {
                path = `
            M ${x0} ${y0}
            H ${x0 + dw}
            V ${y0 + tfs}
            H ${webRight}
            V ${y0 + dh - tfs}
            H ${x0 + dw}
            V ${y0 + dh}
            H ${x0}
            V ${y0 + dh - tfs}
            H ${webLeft}
            V ${y0 + tfs}
            H ${x0}
            Z
        `;
            }

            return (
                <g>
                    {/* SECTION */}
                    <path d={path} fill="rgba(29,78,216,0.10)" stroke="#1d4ed8" strokeWidth="2" strokeLinejoin="round" />
                    {/* Y AXIS */}
                    <line x1="100" y1="12" x2="100" y2="192" stroke="#000" strokeWidth="1" strokeDasharray="5 5" />
                    {/* X AXIS */}
                    <line x1="12" y1="100" x2="188" y2="100" stroke="#000" strokeWidth="1" strokeDasharray="5 5" />
                    {/* Arrow Marker */}
                    <defs>
                        <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,6 L6,3 z" fill="#444" />
                        </marker>
                    </defs>
                    {/* RADIUS */}
                    {draw_r > 0 && (
                        <>
                            <line x1={webLeft - 20} y1={y0 + tfs - 20} x2={webLeft} y2={y0 + tfs} stroke="#444" strokeWidth="1.5" markerEnd="url(#arrow)" />
                            <text x={webLeft - 42} y={y0 + tfs - 14} className="dim-text">R={values.radius}</text>
                        </>
                    )}
                    {/* t */}
                    <text x={x0 + dw + 30} y={y0 + tfs + 10} className="dim-text" transform={`rotate(-90 ${x0 + dw + 30} ${y0 + tfs + 10})`}>t={values.tf}</text>
                    {/* d */}
                    <Dim x1={138} y1={y0 + tfs} x2={138} y2={y0 + dh - tfs} label={`d=${values.h}`} side="right" offset={0} />
                    {/* tw */}
                    <text x="80" y="128" textAnchor="middle" className="dim-text" style={{ fontSize: "10px", fontWeight: "600", fill: "#6b7280" }}>tw={values.tw}</text>
                    {/* b */}
                    <Dim x1={x0} y1={y0 + dh + 20} x2={x0 + dw} y2={y0 + dh + 20} label={`b=${values.w}`} side="bottom" offset={0} />
                </g>
            );
        } else if (section === 'tsection') {
            const isLive = h > 0 && w > 0 && tf > 0 && tw > 0;
            const draw_h = isLive ? h : 2;
            const draw_w = isLive ? w : 1.5;
            const draw_tf = isLive ? tf : 0.2;
            const draw_tw = isLive ? tw : 0.2;

            const ratio = draw_h / draw_w;

            const dw = ratio > 1 ? S / ratio : S;
            const dh = ratio > 1 ? S : S * ratio;

            const tfs = (draw_tf / draw_h) * dh;
            const tws = (draw_tw / draw_w) * dw;

            const x0 = 100 - dw / 2;
            const y0 = 100 - dh / 2;

            const GAP = 12;

            path = `
        M${x0} ${y0}
        h${dw}
        v${tfs}
        h-${(dw - tws) / 2}
        v${dh - tfs}
        h-${tws}
        v-${dh - tfs}
        h-${(dw - tws) / 2}
        z
    `;
            /* b → moved upward */
            labels.push(<Dim key="b" x1={x0} y1={y0 + 190} x2={x0 + dw} y2={y0 + 190} label={`b=${w}`} side="top" />);
            /* d → moved left */
            labels.push(<Dim key="d" x1={x0 - GAP} y1={y0} x2={x0 - GAP} y2={y0 + dh} label={`d=${h}`} side="left" />);
            /* t → more spacing */
            labels.push(<text key="t" x={x0 + dw + 10} y={y0 + tfs / 2} className="dim-text">t={tf}</text>);
            /* tw → more spacing */
            labels.push(<text key="tw" x={x0 + 80} y={y0 + dh - 30} textAnchor="middle" className="dim-text">tw={tw}</text>);
        } else if (section === 'cchannel') {
            const isLive = h > 0 && w > 0 && tf > 0 && tw > 0;

            const draw_b = isLive ? w : 3;
            const draw_d = isLive ? h : 2;
            const draw_t = isLive ? tf : 0.4;
            const draw_tw = isLive ? tw : 0.4;

            const draw_total_h = draw_d + draw_t;
            const draw_total_w = draw_b;

            const ratio = draw_total_h / draw_total_w;

            const dw = ratio > 1 ? S / ratio : S;
            const dh = ratio > 1 ? S : S * ratio;

            const scale = dw / draw_total_w;

            const tfs = draw_t * scale;
            const tws = draw_tw * scale;
            const ds = draw_d * scale;

            const area = draw_b * draw_t + 2 * draw_tw * draw_d;
            const cy_from_top = (draw_b * draw_t * (draw_t / 2) + 2 * draw_tw * draw_d * (draw_t + draw_d / 2)) / area;

            const x0 = 100 - dw / 2;
            const y0 = 100 - cy_from_top * scale;

            path = `
                M ${x0} ${y0}
                h ${dw}
                v ${tfs + ds}
                h -${tws}
                v -${ds}
                h -${dw - 2 * tws}
                v ${ds}
                h -${tws}
                z
            `;

            labels.push(<Dim key="b" x1={x0} y1={y0 - 15} x2={x0 + dw} y2={y0 - 15} label={`b=${w}`} side="top" offset={0} />);
            labels.push(<Dim key="d" x1={x0 + dw - tws - 10} y1={y0 + tfs} x2={x0 + dw - tws - 10} y2={y0 + tfs + ds} label={`d=${h}`} side="left" offset={0} />);

            labels.push(<text key="tw" x={x0 + dw + 10} y={y0 + tfs / 2 + 4} className="dim-text">tw={tf}</text>);
            labels.push(<text key="t1" x={x0 + tws / 2} y={y0 + tfs + ds + 15} textAnchor="middle" className="dim-text">t={tw}</text>);
            labels.push(<text key="t2" x={x0 + dw - tws / 2} y={y0 + tfs + ds + 15} textAnchor="middle" className="dim-text">t={tw}</text>);
        } else if (section === 'lsection') {
            const isLive = b > 0 && h > 0 && t > 0;
            const draw_b = isLive ? b : 1;
            const draw_h = isLive ? h : 1.5;
            const draw_t = isLive ? t : 0.2;

            const ratio = draw_h / draw_b;
            const dw = ratio > 1 ? S / ratio : S;
            const dh = ratio > 1 ? S : S * ratio;

            const ts = (draw_t / Math.max(draw_b, draw_h)) * S * 0.5;

            const x0 = 100 - dw / 2;
            const y0 = 100 - dh / 2;

            // L shape
            path = `
        M${x0} ${y0}
        h${ts}
        v${dh - ts}
        h${dw - ts}
        v${ts}
        h-${dw}
        z
    `;
            /* b → moved downward and away from lines */
            labels.push(<Dim key="b" x1={x0} y1={y0 + dh + 30} x2={x0 + dw} y2={y0 + dh + 30} label={`b=${b}`} />);
            /* d → moved left */
            labels.push(<Dim key="d" x1={x0 - 10} y1={y0} x2={x0 - 10} y2={y0 + dh} label={`d=${h}`} side="left" />);
            /* t → plain text only, no dash */
            labels.push(<text key="t" x={x0 + ts / 2} y={y0 - 10} textAnchor="middle" className="dim-text">t={t}</text>);
        }

        return (
            <g>
                <path d={path} fill="rgba(29, 78, 216, 0.1)" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" />
                {labels}
            </g>
        );
    };

    return (
        <div className="beam-props-pro-page animate-in">
            <Helmet>
                <title>Cross Section Properties Calculator | Professional Section Analysis Tool | OOK</title>
                <meta name="description" content="Calculate section properties like Moment of Inertia (Ixx, Iyy), Section Modulus (Sx, Sy), and Centroid for common engineering shapes." />
            </Helmet>
            <br />
            <br />
            <br />
            <header className="page-hero">
                <div className="breadcrumb">
                    <a href="/">Calculators</a> <span>/</span> <strong>Cross Section Properties</strong>
                </div>
                <h1>Cross Section Properties</h1>
                <p>Instantly calculate cross-sectional area (A), centroid (ȳ), second moment of area (I), section modulus (S), and torsional constant (K) for common structural cross-sections. Includes live annotated visualizations and real-time calculations.</p>
                <br />
                <div className="hero-badges">
                    <div className="hbadge hi">AISC Compliant</div>
                    <div className="hbadge">Real-time Visualization</div>
                </div>
            </header>

            <main className="calc-outer">
                <div className="calc-card">
                    <div className="calc-toolbar">
                        <div className="calc-toolbar-title">Section Analyzer</div>
                    </div>

                    <div className="section-picker">
                        {sectionTypes.map(s => (
                            <button key={s.id} className={`sec-btn ${section === s.id ? 'active' : ''}`} onClick={() => setSection(s.id)}>
                                <svg viewBox="0 0 24 24"><path d={s.icon} stroke="currentColor" /></svg>
                                <span className="sec-btn-label">{s.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="calc-body">
                        <div className="input-panel">
                            <div className="unit-toggle-container">
                                <button
                                    className={`unit-toggle-btn ${units === 'mm' ? 'active' : ''}`}
                                    onClick={() => toggleSystem('mm')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 4v16m-8-8h16" /></svg>
                                    Metric Units
                                </button>
                                <button
                                    className={`unit-toggle-btn ${units === 'in' ? 'active' : ''}`}
                                    onClick={() => toggleSystem('in')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 12h16m-8-8v16" /></svg>
                                    Imperial Units
                                </button>
                            </div>

                            <div className="inp-section-head">Dimensions</div>

                            {section === 'square' && (
                                <div className="field-row Beamproperties">
                                    <span className="field-label">Side <span className="field-code">a</span></span>
                                    <input type="number" value={values.b} onChange={e => handleInput('b', e.target.value)} />
                                    <select className="res-unit-select" value={valueUnits.b} onChange={e => handleUnitChange('b', e.target.value)}>
                                        {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                            )}

                            {(section === 'rectangle' || section === 'rhs' || section === 'ibeam' || section === 'tsection' || section === 'cchannel' || section === 'lsection') && (
                                <div className="field-row Beamproperties">
                                    <span className="field-label">{section === 'cchannel' ? 'Flange Width' : 'Width'} <span className="field-code">b</span></span>
                                    <input type="number" value={section === 'ibeam' || section === 'tsection' || section === 'cchannel' ? values.w : values.b} onChange={e => handleInput(section === 'ibeam' || section === 'tsection' || section === 'cchannel' ? 'w' : 'b', e.target.value)} />
                                    <select className="res-unit-select" value={valueUnits[section === 'ibeam' || section === 'tsection' || section === 'cchannel' ? 'w' : 'b']} onChange={e => handleUnitChange(section === 'ibeam' || section === 'tsection' || section === 'cchannel' ? 'w' : 'b', e.target.value)}>
                                        {section === 'rhs' ? (
                                            (units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)
                                        ) : section === 'rectangle' ? (
                                            (units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)
                                        ) : section === 'lsection' ? (
                                            (units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)
                                        ) : (
                                            (units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)
                                        )}
                                    </select>
                                </div>
                            )}

                            {(section === 'rectangle' || section === 'rhs' || section === 'ibeam' || section === 'tsection' || section === 'cchannel' || section === 'lsection') && (
                                <div className="field-row Beamproperties">
                                    <span className="field-label">{(section === 'lsection' || section === 'rhs' || section === 'rectangle') ? 'Height' : 'Web Height'} <span className="field-code">d</span></span>
                                    <input type="number" value={values.h} onChange={e => handleInput('h', e.target.value)} />
                                    <select className="res-unit-select" value={valueUnits.h} onChange={e => handleUnitChange('h', e.target.value)}>
                                        {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                            )}

                            {section === 'rhs' && (
                                <>
                                    <div className="field-row Beamproperties">
                                        <span className="field-label">Inner Width <span className="field-code">bi</span></span>
                                        <input type="number" value={values.bi} onChange={e => handleInput('bi', e.target.value)} />
                                        <select className="res-unit-select" value={valueUnits.bi} onChange={e => handleUnitChange('bi', e.target.value)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="field-row Beamproperties">
                                        <span className="field-label">Inner Height <span className="field-code">di</span></span>
                                        <input type="number" value={values.hi} onChange={e => handleInput('hi', e.target.value)} />
                                        <select className="res-unit-select" value={valueUnits.hi} onChange={e => handleUnitChange('hi', e.target.value)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                </>
                            )}
                            {section === 'lsection' && (
                                <div className="field-row Beamproperties">
                                    <span className="field-label">Thickness <span className="field-code">t</span></span>
                                    <input type="number" value={values.t} onChange={e => handleInput('t', e.target.value)} />
                                    <select className="res-unit-select" value={valueUnits.t} onChange={e => handleUnitChange('t', e.target.value)}>
                                        {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                            )}

                            {(section === 'circle' || section === 'pipe') && (
                                <div className="field-row Beamproperties">
                                    <span className="field-label">Radius <span className="field-code">{section === 'pipe' ? 'r' : 'r'}</span></span>
                                    <input type="number" value={values.r} onChange={e => handleInput('r', e.target.value)} />
                                    <select className="res-unit-select" value={valueUnits.r} onChange={e => handleUnitChange('r', e.target.value)}>
                                        {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                            )}

                            {section === 'pipe' && (
                                <div className="field-row Beamproperties">
                                    <span className="field-label">Inner Radius <span className="field-code">ri</span></span>
                                    <input type="number" value={values.ri} onChange={e => handleInput('ri', e.target.value)} />
                                    <select className="res-unit-select" value={valueUnits.ri} onChange={e => handleUnitChange('ri', e.target.value)}>
                                        {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                            )}

                            {(section === 'ibeam' || section === 'tsection' || section === 'cchannel') && (
                                <>
                                    <div className="field-row Beamproperties">
                                        <span className="field-label">{section === 'cchannel' ? 'Web Thick.' : 'Flange Thick.'} <span className="field-code">{section === 'cchannel' ? 'tw' : 't'}</span></span>
                                        <input type="number" value={values.tf} onChange={e => handleInput('tf', e.target.value)} />
                                        <select className="res-unit-select" value={valueUnits.tf} onChange={e => handleUnitChange('tf', e.target.value)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="field-row Beamproperties">
                                        <span className="field-label">{section === 'cchannel' ? 'Flange Thick.' : 'Web Thick.'} <span className="field-code">{section === 'cchannel' ? 't' : 'tw'}</span></span>
                                        <input type="number" value={values.tw} onChange={e => handleInput('tw', e.target.value)} />
                                        <select className="res-unit-select" value={valueUnits.tw} onChange={e => handleUnitChange('tw', e.target.value)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    {section === 'ibeam' && (
                                        <div className="field-row Beamproperties">
                                            <span className="field-label">Radius <span className="field-code">r</span></span>
                                            <input type="number" value={values.radius} onChange={e => handleInput('radius', e.target.value)} />
                                            <select className="res-unit-select" value={valueUnits.radius} onChange={e => handleUnitChange('radius', e.target.value)}>
                                                {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}

                            <button className="calc-btn">
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 2l10 10M12 2L2 12" /></svg>
                                Calculate Properties
                            </button>
                        </div>

                        <div className="results-panel">
                            <div className="diagram-wrap">
                                <span className="diagram-label">Live Preview</span>
                                <svg id="section-svg" viewBox="0 0 240 260">
                                    <defs>
                                        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="var(--border)" strokeWidth="0.5" />
                                        </pattern>
                                        <marker id="arrow-start" markerWidth="10" markerHeight="10" refX="0" refY="5" orient="auto">
                                            <path d="M 10 0 L 0 5 L 10 10 Z" fill="var(--muted)" />
                                        </marker>
                                        <marker id="arrow-end" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
                                            <path d="M 0 0 L 10 5 L 0 10 Z" fill="var(--muted)" />
                                        </marker>
                                    </defs>
                                    <rect width="200" height="200" fill="url(#grid)" />

                                    {renderDiagram()}

                                    {results && (
                                        <g>
                                            <g stroke="var(--red)" strokeWidth="1" strokeDasharray="4 2">
                                                <line x1="10" y1="100" x2="190" y2="100" />
                                                <line x1="100" y1="10" x2="100" y2="190" />
                                            </g>
                                            <text x="195" y="103" className="svg-axis-label">X</text>
                                            <text x="103" y="15" className="svg-axis-label">Y</text>
                                        </g>
                                    )}
                                </svg>
                            </div>

                            {results ? (
                                <div className="results-grid">
                                    {!(section === 'circle' || section === 'pipe') && (
                                        <div className="result-card accent">
                                            <div className="res-label">Area (A)</div>
                                            <div className="res-value big">{fmt(results.A, 'area', resultUnits.A)}</div>
                                            <select className="res-unit-select" value={resultUnits.A} onChange={e => handleUnitChange('A', e.target.value, true)}>
                                                {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).area.map(u => <option key={u} value={u}>{u}</option>)}
                                            </select>
                                        </div>
                                    )}

                                    <div className="result-card">
                                        <div className="res-label">Centroid (Xc)</div>
                                        <div className="res-value">{fmt(results.xbar, 'length', resultUnits.xbar)}</div>
                                        <select className="res-unit-select" value={resultUnits.xbar} onChange={e => handleUnitChange('xbar', e.target.value, true)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="result-card">
                                        <div className="res-label">Centroid (Yc)</div>
                                        <div className="res-value">{fmt(results.ybar, 'length', resultUnits.ybar)}</div>
                                        <select className="res-unit-select" value={resultUnits.ybar} onChange={e => handleUnitChange('ybar', e.target.value, true)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).length.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="result-card">
                                        <div className="res-label">Inertia (Ix)</div>
                                        <div className="res-value">{fmt(results.Ixx, 'inertia', resultUnits.Ixx)}</div>
                                        <select className="res-unit-select" value={resultUnits.Ixx} onChange={e => handleUnitChange('Ixx', e.target.value, true)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).inertia.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="result-card">
                                        <div className="res-label">Section Modulus (Sx)</div>
                                        <div className="res-value">{fmt(results.Sx, 'sectionModulus', resultUnits.Sx)}</div>
                                        <select className="res-unit-select" value={resultUnits.Sx} onChange={e => handleUnitChange('Sx', e.target.value, true)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).sectionModulus.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="result-card">
                                        <div className="res-label">Inertia (Iy)</div>
                                        <div className="res-value">{fmt(results.Iyy, 'inertia', resultUnits.Iyy)}</div>
                                        <select className="res-unit-select" value={resultUnits.Iyy} onChange={e => handleUnitChange('Iyy', e.target.value, true)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).inertia.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="result-card">
                                        <div className="res-label">Section Modulus (Sy)</div>
                                        <div className="res-value">{fmt(results.Sy, 'sectionModulus', resultUnits.Sy)}</div>
                                        <select className="res-unit-select" value={resultUnits.Sy} onChange={e => handleUnitChange('Sy', e.target.value, true)}>
                                            {(units === 'mm' ? UNIT_OPTIONS.metric : UNIT_OPTIONS.imperial).sectionModulus.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    {!(section === 'rhs' ||
                                        section === 'tsection' ||
                                        section === 'cchannel' ||
                                        section === 'pipe' ||
                                        section === 'lsection') && (
                                            <div className="result-card span2 accent">
                                                <div className="res-label">Torsional Constant (K)</div>
                                                <div className="res-value">{fmt(results.J, 'inertia', resultUnits.J)}</div>
                                                <select
                                                    className="res-unit-select"
                                                    value={resultUnits.J}
                                                    onChange={e => handleUnitChange('J', e.target.value, true)}
                                                >
                                                    {(units === 'mm'
                                                        ? UNIT_OPTIONS.metric
                                                        : UNIT_OPTIONS.imperial
                                                    ).inertia.map(u => (
                                                        <option key={u} value={u}>{u}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                </div>
                            ) : (
                                <div className="empty-results">
                                    <div className="empty-icon">
                                        <svg viewBox="0 0 24 24"><path d="M12 8v4l3 3" /><circle cx="12" cy="12" r="9" /></svg>
                                    </div>
                                    <p>Enter dimensions to see results</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <section className="beam-content">
                <div className="sec-label">Section Properties</div>
                <h2 className="sec-title">What Each Cross Section Property <em>Means</em></h2>
                <p className="sec-lead">Cross-section properties are geometric quantities that determine how a structural member resists axial force, bending, shear, and torsion. They depend entirely on the shape and dimensions of the cross-section — not on the material. Understanding each property is essential for selecting, sizing, and checking beams, columns, and other structural elements.</p>

                <div className="props-grid">
                    <div className="prop-card aa">
                        <div className="prop-sym">A</div>
                        <div className="prop-name">Cross-Sectional Area</div>
                        <div className="prop-formula">A = ∫dA  [mm² or in²]</div>
                        <p className="prop-body">Total amount of space inside the section. Used for calculations of stress, strain, and moment of inertia. A larger area directly reduces axial and average shear stresses.</p>
                    </div>
                    <div className="prop-card ixx">
                        <div className="prop-sym">Ix, Iy</div>
                        <div className="prop-name">Second Moment of Area</div>
                        <div className="prop-formula">Ix = ∫y² dA, Iy = ∫x² dA [mm⁴ or in⁴]</div>
                        <p className="prop-body">Geometrical property that reflects how the area of a cross-section is distributed relative to a particular axis and measure of cross-section resistance to bending due to its shape. Also called the moment of inertia.</p>
                    </div>
                    <div className="prop-card sx">
                        <div className="prop-sym">Sx, Sy</div>
                        <div className="prop-name">Section Modulus</div>
                        <div className="prop-formula">S = I / c [mm³ or in³]</div>
                        <p className="prop-body">Geometrical property used to design flexural members. Measures section's ability to resist bending. Tells us about the strength of the beam; higher modulus means higher strength. For asymmetric sections, the smaller value governs.</p>
                    </div>
                    <div className="prop-card cent">
                        <div className="prop-sym">Xc, Yc</div>
                        <div className="prop-name">Centroid (Center of mass)</div>
                        <div className="prop-formula">Xc = Σ(Ax)/ΣA, Yc = Σ(Ay)/ΣA [mm or in]</div>
                        <p className="prop-body">Center of mass for the geometric shape where the area could be balanced without rotation. Crucial for designing stable structures and determining the location of the neutral axis.</p>
                    </div>
                    <div className="prop-card jj">
                        <div className="prop-sym">K</div>
                        <div className="prop-name">Torsional Constant</div>
                        <div className="prop-formula">K = TL / (G·ϕ) [mm⁴ or in⁴]</div>
                        <p className="prop-body">Geometrical property describing resistance to torsional deformation. Crucial for designing shafts that transmit power to ensure they transfer torque without excessive twisting or failure.</p>
                    </div>
                    <div className="prop-card axis">
                        <div className="prop-sym">X, Y</div>
                        <div className="prop-name">Principal Axis</div>
                        <div className="prop-formula">Intersection at Centroid</div>
                        <p className="prop-body">The main axes of a cross-section which are perpendicular and intersect each other at the centroid. These axes represent the strong and weak directions of the member.</p>
                    </div>
                </div>

                <div className="sec-label" style={{ marginTop: '4rem' }}>Formula Reference</div>
                <h2 className="sec-title">Detailed Cross Section <em>Formulas</em></h2>

                <h3 className="formula-category-title">Area & Centroid</h3>
                <div className="formula-table-wrap">
                    <table className="formula-table">
                        <thead>
                            <tr>
                                <th>Section Type</th>
                                <th>Area (A)</th>
                                <th>Centroid (Xc, Yc)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td>Square</td><td className="mono">a²</td><td className="mono">a/2, a/2</td></tr>
                            <tr><td>Rectangle</td><td className="mono">b · d</td><td className="mono">b/2, d/2</td></tr>
                            <tr><td>Hollow Rect</td><td className="mono">b·d − bi·di</td><td className="mono">b/2, d/2</td></tr>
                            <tr><td>I-Beam</td><td className="mono">t·d + 2·t·b</td><td className="mono">b/2, d/2+tf</td></tr>
                            <tr><td>T-Section</td><td className="mono">(b * tf) + (d * tw)</td><td className="mono">b/2, (b·tf² + tf·d(2·tf+d)) / 2(tf·b + tw·d)</td></tr>
                            <tr><td>C-Channel</td><td className="mono">b·t + 2·tw·d</td><td className="mono">b/2, (b·t² + 2·tw·d·(2·t + d)) / 2(t·b + 2·tw·d)</td></tr>
                            <tr><td>L-Section</td><td className="mono">t·(b + d − t)</td><td className="mono">(b²+d·t−t²) / 2(b+d−t), (d²+b·t−t²) / 2(b+d−t)</td></tr>
                            <tr><td>Solid Circle</td><td className="mono">π·r²</td><td className="mono">r, r</td></tr>
                            <tr><td>Pipe</td><td className="mono">π(r−ri)</td><td className="mono">r, r</td></tr>
                        </tbody>
                    </table>
                </div>

                <h3 className="formula-category-title">Moment of Inertia</h3>
                <div className="formula-table-wrap">
                    <table className="formula-table">
                        <thead>
                            <tr>
                                <th>Section Type</th>
                                <th>Ix (X-Axis)</th>
                                <th>Iy (Y-Axis)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td>Square</td><td className="mono">a⁴ / 12</td><td className="mono">a⁴ / 12</td></tr>
                            <tr><td>Rectangle</td><td className="mono">b·d³ / 12</td><td className="mono">d·b³ / 12</td></tr>
                            <tr><td>Hollow Rect</td><td className="mono">(b·d³ − bi·di³) / 12</td><td className="mono">(d·b³ − di·bi³) / 12</td></tr>
                            <tr><td>I-Beam</td><td className="mono">(b(d+2tf)³- (b-tw)d³) / 12</td><td className="mono">((b³·tf) / 6) + ((tw³·tf) / 12)</td></tr>
                            <tr><td>T-Section</td><td className="mono">((b(d+tf)³- d³(b-tw))/3) - A(d+tw-yc)²</td><td className="mono">(tf·b³ + d·tw³) / 12</td></tr>
                            <tr><td>C-Channel</td><td className="mono">((b(d+t)³ − d³(b−2tw))/3) − A(d+t−Yc)²</td><td className="mono">((d+t)b³ − d(b−2tw)³) / 12</td></tr>
                            <tr><td>L-Section</td><td className="mono">(b·d³ − (b−t)(d−t)³)/3 − A(d−Yc)²</td><td className="mono">(d·b³ − (d−t)(b−t)³)/3 − A(b−Xc)²</td></tr>
                            <tr><td>Solid Circle</td><td className="mono">π·r⁴ / 4</td><td className="mono">π·r⁴ / 4</ td></tr>
                            <tr><td>Pipe (r, ri)</ td>< td className="mono">π(r⁴−ri⁴) / 4</ td >< td className="mono">π(r⁴−ri⁴) / 4</ td ></ tr >
                        </tbody>
                    </table>
                </div>

                <h3 className="formula-category-title">Section Modulus</h3>
                <div className="formula-table-wrap">
                    <table className="formula-table">
                        <thead>
                            <tr>
                                <th>Section Type</th>
                                <th>Sx (Elastic)</th>
                                <th>Sy (Elastic)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td>Square</td><td className="mono">a³ / 6</td><td className="mono">a³ / 6</td></tr>
                            <tr><td>Rectangle</td><td className="mono">b.d²/6</td><td className="mono">d.b²/6</td></tr>
                            <tr><td>Hollow Rect</td><td className="mono">Ix / Yc</td><td className="mono">Iy / Xc</td></tr>
                            <tr><td>I-Beam</td><td className="mono">Ix / Yc <br /> </td><td className="mono">Iy / Xc</td></tr>
                            <tr><td>T-Section</td><td className="mono">Ix/b+tf-Yc</td><td className="mono">Iy / Xc</td></tr>
                            <tr><td>C-Channel</td><td className="mono">Ix / (d + t − Yc)</td><td className="mono">Iy / Xc</td></tr>
                            <tr><td>L-Section</td><td className="mono">Ix / (d − Yc)</td><td className="mono">Iy / (b − Xc)</td></tr>
                            <tr><td>Solid Circle</td><td className="mono">Ix / Yc <br /> π.R3/4</td><td className="mono">Ix / Yc <br /> π.R3/4</td></tr>
                            <tr><td>Pipe</td><td className="mono">Ix/Yc</td><td className="mono">Ix/Yc</td></tr>
                        </tbody>
                    </table>
                </div>

                <h3 className="formula-category-title">Torsional Properties</h3>
                <div className="formula-table-wrap">
                    <table className="formula-table">
                        <thead>
                            <tr>
                                <th>Section Type</th>
                                <th>K (Torsional Constant)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td>Square</td><td className="mono">9·a⁴ / 64</td></tr>
                            <tr>
                                <td>Rectangle</td>


                                <td className="mono">
                                    K = (ab³/3) - 0.21b⁴ + (0.0175 b⁸/a⁴)
                                    <br />
                                    K = 2K₁ + K₂ + 2αD⁴
                                    <br />
                                    K₁ = (b·t³/3) - 0.21t⁴ + (0.0175 t⁸/b⁴)
                                    <br />
                                    K₂ = d·tw³/3
                                </td>
                            </tr>
                            <tr>
                                <td>I-Beam</td>
                                <td className="mono">
                                    D = ((t+r)² + rtw + (tw²/ 4)) /(2r+t)
                                    <br />
                                    α = t1/t2(0.15 + 0.1 r/b)
                                    <br />
                                    If t <LiaLessThanSolid style={{ fontSize: '1vw' }} /> tw, then: t1/t2 = t/tw
                                    <br />
                                    If tw <LiaLessThanSolid style={{ fontSize: '1vw' }} /> t, then: t2/t1 = tw/t
                                </td>
                            </tr>
                            <tr><td>Solid Circle</td><td className="mono">π·r⁴ / 2</td></tr>
                        </tbody>
                    </table>
                </div>

                <div className="sec-label" style={{ marginTop: '4rem' }}>Engineering Applications</div>
                <h2 className="sec-title">How Cross Section Properties Are <em>Used in Design</em></h2>
                <br />
                <div className="two-col">
                    <div className="content-prose">
                        <h3>Beam Design — Bending Stress</h3>
                        <p>The maximum bending stress in a beam subjected to a bending moment M is determined using the flexure formula:</p>
                        <div
                            style={{
                                fontSize: "0.9vw",
                                fontFamily: "Times New Roman, serif",
                            }}
                        >
                            σ = <span style={{ borderBottom: "2px solid white" }}>M</span> / S
                        </div>

                        <p
                            style={{
                                fontSize: "1vw",
                                maxWidth: "1200px",
                            }}
                        >
                            For Allowable Stress Design (ASD), the required section modulus is:
                        </p>

                        {/* Formula 2 */}
                        <div
                            style={{
                                fontSize: "0.9vw",
                                fontFamily: "Times New Roman, serif",
                            }}
                        >
                            Sₓ ≥ Mₘₐₓ / F_b
                        </div>
                        <p>where <i>F<sub>b</sub></i> is the allowable bending stress.</p>
                        <p>The section modulus <i>S</i> represents the efficiency of a cross-section in resisting bending stresses. A larger section modulus results in lower bending stress for the same applied moment.</p>

                        <h3>Beam Design — Deflection</h3>
                        <p>Beam deflection is governed by the flexural stiffness <i>EI</i>. For a simply supported beam with a central point load, the maximum deflection is:</p>
                        {/* Formula */}
                        <div
                            style={{
                                fontSize: "0.9vw",
                                fontFamily: "Times New Roman, serif",
                            }}
                        >
                            δ = PL³ / 48EI
                        </div>
                        <p>The moment of inertia <i>I<sub>xx</sub></i> appears in the denominator, meaning that increasing <i>I</i> significantly reduces beam deflection. Since <i>I</i> scales approximately with <i>h³</i> for rectangular sections, increasing beam depth is substantially more effective than increasing width for deflection control.</p>

                    </div>
                    <div className="content-prose">
                        <h3>Column Design — Buckling</h3>
                        <p>The Euler critical buckling load of a column is given by:</p>
                        {/* Formula */}
                        <div
                            style={{
                                fontSize: "0.9vw",
                                fontFamily: "Times New Roman, serif",
                            }}
                        >
                            P<sub>cr</sub> = π²EI / (KL)²
                        </div>

                        <p>Engineers use this equation to estimate the maximum axial compressive load a column can sustain before buckling instability occurs. The moment of inertia <i>I<sub>xx</sub></i> directly influences buckling resistance — sections with larger <i>I</i> values provide greater resistance to instability and improve structural performance under compressive loading.</p>

                    </div>
                </div>

                <div className="sec-label" style={{ marginTop: '4rem' }}>FAQ & Analysis</div>
                <h2 className="sec-title">Common <em>Engineering Questions</em></h2>
                <div className="faq-grid">
                    {[
                        { q: "What is the second moment of area (moment of inertia)?", a: "The second moment of area (I) is a geometric property of a cross-section that quantifies its resistance to bending. A higher value means greater stiffness and smaller deflection under the same applied moment. It has units of length to the fourth power (mm⁴ or in⁴) and is always measured about a specific axis through the centroid." },
                        { q: "What is the difference between Ix and Iy?", a: "Ix and Iy represent the moments of inertia about the horizontal and vertical centroidal axes, respectively. Most beams are designed to resist bending about the 'strong axis' (usually Ix) where the material is distributed furthest from the axis." },
                        { q: "What is the torsional constant (K)?", a: "The torsional constant (K) is a geometric property that quantifies a section's resistance to twisting. For circular sections, it is equal to the polar moment of inertia. For open sections like I-beams and channels, K is much smaller and represents the section's resistance to pure torsion." },
                        { q: "What is the parallel axis theorem?", a: "The parallel axis theorem states that the moment of inertia about any axis equals the centroidal moment of inertia plus the product of the area and the square of the distance between the two axes: I = Ic + A·d². It is essential for calculating properties of composite sections like I-beams and T-sections by breaking them into rectangles." },
                        { q: "Why is the I-beam shape so efficient for bending?", a: "The I-beam concentrates material in the flanges, as far as possible from the neutral axis. Because moment of inertia increases with the square of the distance from the axis (via the parallel axis theorem), this maximises Ix for a given cross-sectional area. The web separates the flanges and carries shear, but contributes little to bending resistance." },
                        { q: "What is the shape factor in structural engineering?", a: "The shape factor (f = Z/S) is the ratio of the plastic section modulus to the elastic section modulus. It represents the reserve bending strength beyond first yield. For a typical W-shape I-beam, f ≈ 1.12–1.15." },
                        { q: "Why the I-Beam is the Standard Structural Section Standard Structural Section?", a: "The I-beam (wide flange / W-section) dominates structural steel design because it achieves the best ratio of bending stiffness to weight. The two flanges concentrate mass far from the neutral axis, maximising Ixx through the parallel axis theorem. The web is sized to carry shear and separate the flanges, but contributes minimally to Ixx. For a typical W-section, over 70% of Ixx comes from the flanges, not the web." },
                    ].map((item, i) => (
                        <div key={i} className={`faq-item ${activeFaq === i ? 'open' : ''}`}>
                            <button className="faq-q" onClick={() => setActiveFaq(activeFaq === i ? -1 : i)}>
                                <span>{item.q}</span>
                                <ChevronDown className="chevron" size={16} />
                            </button>
                            <div className={`faq-a ${activeFaq === i ? 'open' : ''}`}>{item.a}</div>
                        </div>
                    ))}
                </div>

            </section>
        </div>
    );
};

export default BeamPropertiesPro;
